import { Phone } from "@/interfaces/phone";
import axios from "@/lib/axios";
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/vue-query";
import { update } from "lodash";
import { ToastInterface, useToast } from "vue-toastification";

interface Params extends Omit<Partial<Phone>, "id"> {
  phoneId?: string | number;
  countryCodeId?: string | number;
  showToasts?: boolean;
}

interface Props {
  immediate?: boolean;
  params?: Params;
}

type UpdateParams = Omit<Params, "id">;

const getUserPhone = async (params?: Params): Promise<Phone | void> => {
  if (params) {
    const { phoneId } = params;
    const { data } = await axios.get(`/api/phones/${phoneId}`);

    return data;
  }
};

const updateUserPhone = async (
  params: UpdateParams,
  toast: ToastInterface,
): Promise<Phone | undefined> => {
  try {
    const { phoneId, showToasts = true } = params;
    const { data } = await axios.put(`/api/phones/${phoneId}`, params);

    if (showToasts) {
      toast.success("Teléfono actualizado con éxito");
    }
    return data;
  } catch (err: any) {
    toast.error(err.response.data.message);
    return undefined;
  }
};

const deleteUserPhone = async (
  params: Required<Pick<Params, "phoneId">>,
  toast: ToastInterface,
  queryClient: QueryClient,
): Promise<void> => {
  try {
    const { phoneId } = params;
    await axios.delete(`/api/phones/${phoneId}`);

    queryClient.invalidateQueries(["phone", phoneId]);
    toast.success("Teléfono eliminado con éxito");
  } catch (err: any) {
    toast.error(err.response.data.message);
  }
};

const usePhoneRequest = (props: Props) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { immediate = true, params } = props || {};

  const { data, isLoading } = useQuery(
    ["phone", params?.phoneId],
    () => getUserPhone(params),
    {
      enabled: immediate,
      refetchOnWindowFocus: true,
      retry: false,
    },
  );

  const updateMutation = useMutation((params: UpdateParams) =>
    updateUserPhone(params, toast),
  );

  const deleteMutation = useMutation(
    (params: Required<Pick<Params, "phoneId">>) =>
      deleteUserPhone(params, toast, queryClient),
  );

  return {
    phone: data,
    isLoading,

    updatePhoneMutation: updateMutation,
    updatePhone: updateMutation.mutate,
    updatePhoneAsync: updateMutation.mutateAsync,
    updatePhoneLoading: updateMutation.isLoading,
    updatePhoneError: updateMutation.error,

    deletePhoneMutation: deleteMutation,
    deletePhone: deleteMutation.mutate,
    deletePhoneAsync: deleteMutation.mutateAsync,
  };
};

export default usePhoneRequest;
